import { useAuth } from '@/hooks/useAuth';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/firebase/config';
import { Loader2, Landmark, Plus, Search, X, Users, Pencil, Trash2, Palette, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RichTextarea } from '@/components/ui/rich-textarea';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { createOrganizacion, saveOrganizacionSettings, adminDeleteOrganizacion, searchUsersByNickname } from '@/lib/actions';
import type { Organizacion } from '@/lib/types';

type FoundUser = { id: string; nickname: string; profileImageUrl: string | null };

function useUserNames(uids: string[]) {
  const [names, setNames] = useState<Record<string, string>>({});
  useEffect(() => {
    const missing = uids.filter((u) => !names[u]);
    if (missing.length === 0) return;
    (async () => {
      try {
        const fn = httpsCallable<{ uids: string[] }, { names: Record<string, string> }>(functions, 'getPublicUserNames');
        const res = await fn({ uids: missing });
        setNames((prev) => ({ ...prev, ...(res.data?.names || {}) }));
      } catch (e) { console.error(e); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uids.join(',')]);
  return names;
}

function GestoresManager({ uids, names, onAdd, onRemove, busyUid }: {
  uids: string[]; names: Record<string, string>; onAdd: (u: FoundUser) => void; onRemove: (uid: string) => void; busyUid: string | null;
}) {
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<FoundUser[]>([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const q = term.trim();
    if (q.length < 2) { setResults([]); setSearching(false); return; }
    setSearching(true);
    const t = setTimeout(async () => { const { users } = await searchUsersByNickname(q); setResults(users || []); setSearching(false); }, 350);
    return () => clearTimeout(t);
  }, [term]);

  return (
    <div className="rounded-2xl border border-white/8 bg-card/40 p-4">
      <div className="mb-3 flex items-center gap-2"><Users className="h-4 w-4 text-violet-400" /><h4 className="font-semibold">Acceso (gestores)</h4><span className="ml-auto text-xs text-muted-foreground">{uids.length}</span></div>
      <div className="relative mb-2">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Buscar usuario para dar acceso..." className="pl-9" />
        {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />}
      </div>
      <AnimatePresence>
        {results.length > 0 && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mb-3 space-y-1 overflow-hidden">
            {results.map((u) => {
              const already = uids.includes(u.id);
              return (
                <div key={u.id} className="flex items-center gap-2 rounded-xl border border-white/8 bg-muted/30 p-2">
                  <Avatar className="h-8 w-8"><AvatarImage src={u.profileImageUrl || undefined} /><AvatarFallback>{u.nickname?.charAt(0)?.toUpperCase()}</AvatarFallback></Avatar>
                  <span className="flex-1 truncate text-sm">{u.nickname}</span>
                  <Button size="sm" variant={already ? 'ghost' : 'secondary'} disabled={already || busyUid === u.id} onClick={() => onAdd(u)}>{busyUid === u.id ? <Loader2 className="h-4 w-4 animate-spin" /> : already ? 'Ya está' : 'Añadir'}</Button>
                </div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
      {uids.length === 0 ? <p className="py-3 text-center text-xs text-muted-foreground">Sin gestores. Nadie puede gestionar esta organización todavía.</p> : (
        <div className="space-y-1">
          {uids.map((uid) => (
            <div key={uid} className="flex items-center gap-2 rounded-xl border border-white/6 bg-muted/20 p-2">
              <Avatar className="h-8 w-8"><AvatarFallback>{(names[uid] || '?').charAt(0).toUpperCase()}</AvatarFallback></Avatar>
              <span className="flex-1 truncate text-sm">{names[uid] || uid}</span>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:bg-destructive/10" disabled={busyUid === uid} onClick={() => onRemove(uid)}>{busyUid === uid ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}</Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function OrgEditor({ org, onClose, onChanged }: { org: Organizacion; onClose: () => void; onChanged: () => void }) {
  const { toast } = useToast();
  const [form, setForm] = useState({ nombreCompleto: org.nombreCompleto || '', escudoUrl: org.escudoUrl || '', bio: org.bio || '', colorPrimario: org.colorPrimario || '#a855f7', colorSecundario: org.colorSecundario || '#000000' });
  const [saving, setSaving] = useState(false);
  const [busyUid, setBusyUid] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const set = (k: string, v: any) => setForm((p) => ({ ...p, [k]: v }));
  const names = useUserNames(org.gestores || []);

  const saveInfo = async () => {
    setSaving(true);
    const res = await saveOrganizacionSettings(org.id, { nombreCompleto: form.nombreCompleto.trim(), escudoUrl: form.escudoUrl.trim(), bio: form.bio, colorPrimario: form.colorPrimario, colorSecundario: form.colorSecundario });
    if (res.success) { toast({ title: 'Guardado', description: res.message }); onChanged(); } else toast({ title: 'Error', description: res.message, variant: 'destructive' });
    setSaving(false);
  };

  const gestorOp = async (uid: string, method: 'arrayUnion' | 'arrayRemove', msg: string) => {
    setBusyUid(uid);
    const res = await saveOrganizacionSettings(org.id, { gestores: { _method: method, elements: [uid] } } as any);
    if (res.success) { toast({ title: 'Hecho', description: msg }); onChanged(); } else toast({ title: 'Error', description: res.message, variant: 'destructive' });
    setBusyUid(null);
  };

  const del = async () => {
    setDeleting(true);
    const res = await adminDeleteOrganizacion(org.id);
    if (res.success) { toast({ title: 'Eliminada', description: res.message }); onChanged(); onClose(); } else { toast({ title: 'Error', description: res.message, variant: 'destructive' }); setDeleting(false); }
  };

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="flex w-full flex-col p-0 sm:max-w-xl">
        <SheetHeader className="border-b border-white/8 px-6 pb-3 pt-6">
          <div className="flex items-center gap-3">
            <Avatar className="h-11 w-11 border border-white/10"><AvatarImage src={form.escudoUrl} /><AvatarFallback>{form.nombreCompleto.charAt(0)}</AvatarFallback></Avatar>
            <div className="min-w-0"><SheetTitle className="truncate">{form.nombreCompleto || 'Organización'}</SheetTitle><SheetDescription>Edita la información y el acceso.</SheetDescription></div>
          </div>
        </SheetHeader>
        <ScrollArea className="min-h-0 flex-1">
          <div className="space-y-5 px-6 py-4">
            <div className="space-y-1.5"><Label>Nombre</Label><Input value={form.nombreCompleto} onChange={(e) => set('nombreCompleto', e.target.value)} /></div>
            <div className="space-y-1.5"><Label>URL del escudo</Label><div className="flex items-center gap-3"><Input value={form.escudoUrl} onChange={(e) => set('escudoUrl', e.target.value)} placeholder="https://..." />{form.escudoUrl && <img src={form.escudoUrl} alt="" className="h-10 w-10 shrink-0 rounded-lg border border-white/10 object-cover" />}</div></div>
            <div className="space-y-2">
              <p className="flex items-center gap-2 text-sm font-semibold text-muted-foreground"><Palette className="h-4 w-4" /> Colores</p>
              <div className="flex gap-6">
                <div className="space-y-1.5"><Label className="text-xs">Primario</Label><Input type="color" value={form.colorPrimario} onChange={(e) => set('colorPrimario', e.target.value)} className="h-11 w-20 p-1" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Secundario</Label><Input type="color" value={form.colorSecundario} onChange={(e) => set('colorSecundario', e.target.value)} className="h-11 w-20 p-1" /></div>
              </div>
            </div>
            <div className="space-y-1.5"><Label>Biografía</Label><RichTextarea rows={3} value={form.bio} onChange={(e) => set('bio', e.target.value)} /></div>
            <Button onClick={saveInfo} disabled={saving} className="w-full">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Guardar cambios</Button>

            <GestoresManager uids={org.gestores || []} names={names} busyUid={busyUid}
              onAdd={(u) => gestorOp(u.id, 'arrayUnion', `${u.nickname} ahora gestiona esta organización.`)}
              onRemove={(uid) => gestorOp(uid, 'arrayRemove', 'Acceso revocado.')} />

            <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-4">
              <p className="mb-1 flex items-center gap-2 text-sm font-semibold text-destructive"><AlertTriangle className="h-4 w-4" /> Zona peligrosa</p>
              <p className="mb-3 text-xs text-muted-foreground">Eliminar la organización es permanente.</p>
              <AlertDialog>
                <AlertDialogTrigger asChild><Button variant="destructive" disabled={deleting} className="gap-2">{deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Eliminar organización</Button></AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader><AlertDialogTitle>¿Eliminar "{form.nombreCompleto}"?</AlertDialogTitle><AlertDialogDescription>Esta acción es permanente.</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={del} className="bg-destructive hover:bg-destructive/80">Eliminar</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

function CreateOrgDialog({ onCreated }: { onCreated: () => void }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [nombreCompleto, setNombreCompleto] = useState('');
  const [escudoUrl, setEscudoUrl] = useState('');

  const create = async () => {
    if (nombreCompleto.trim().length < 3) { toast({ title: 'Nombre demasiado corto', variant: 'destructive' }); return; }
    setCreating(true);
    const res = await createOrganizacion({ nombreCompleto: nombreCompleto.trim(), escudoUrl: escudoUrl.trim() || undefined });
    if (res.success) { toast({ title: 'Organización creada', description: res.message }); setNombreCompleto(''); setEscudoUrl(''); setOpen(false); onCreated(); } else toast({ title: 'Error', description: res.message, variant: 'destructive' });
    setCreating(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="gap-2"><Plus className="h-4 w-4" /> Crear organización</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Nueva organización</DialogTitle><DialogDescription>Crea la organización; luego podrás editar el resto y dar acceso.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label>Nombre *</Label><Input value={nombreCompleto} onChange={(e) => setNombreCompleto(e.target.value)} placeholder="Ej: Consejo de Cofradías" /></div>
          <div className="space-y-1.5"><Label>URL del escudo (opcional)</Label><Input value={escudoUrl} onChange={(e) => setEscudoUrl(e.target.value)} placeholder="https://..." /></div>
        </div>
        <DialogFooter><DialogClose asChild><Button variant="ghost">Cancelar</Button></DialogClose><Button onClick={create} disabled={creating}>{creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Crear</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminOrganizacionesPage() {
  const { userData, loading, jartinetData, refetch } = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);

  useEffect(() => { if (!loading && userData?.role !== 'admin') navigate('/jartinet', { replace: true }); }, [userData, loading, navigate]);

  // Todas las organizaciones excepto el Arzobispado (que va aparte).
  const orgs = useMemo(() => {
    const list = (jartinetData?.organizaciones || []).filter((o) => o.nombreCompleto !== 'Arzobispado');
    const q = search.trim().toLowerCase();
    const sorted = [...list].sort((a, b) => (a.nombreCompleto || '').localeCompare(b.nombreCompleto || ''));
    return q ? sorted.filter((o) => o.nombreCompleto?.toLowerCase().includes(q)) : sorted;
  }, [jartinetData, search]);

  const editing = useMemo(() => (jartinetData?.organizaciones || []).find((o) => o.id === editingId) || null, [jartinetData, editingId]);

  if (loading || userData?.role !== 'admin') return <div className="flex h-[60vh] items-center justify-center"><Loader2 className="h-10 w-10 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-8 pb-12">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="relative overflow-hidden rounded-3xl border border-white/8 bg-gradient-to-br from-violet-500/15 via-card/40 to-card/20 p-8">
        <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-violet-500/20 blur-3xl" />
        <div className="relative flex flex-wrap items-center justify-between gap-5">
          <div className="flex items-center gap-5">
            <div className="grid h-16 w-16 place-items-center rounded-2xl border border-violet-400/30 bg-violet-500/15 text-violet-300"><Landmark className="h-9 w-9" /></div>
            <div><h1 className="font-headline text-4xl font-bold md:text-5xl">Organizaciones</h1><p className="mt-2 text-muted-foreground">Crea organizaciones, edita su info y decide quién tiene acceso a cada una.</p></div>
          </div>
          <CreateOrgDialog onCreated={refetch} />
        </div>
      </motion.div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar organización..." className="pl-9" />
      </div>

      {orgs.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-white/8 py-16 text-muted-foreground"><Landmark className="mb-3 h-10 w-10 opacity-40" /><p className="text-sm">No hay organizaciones{search ? ' que coincidan' : ''}.</p></div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {orgs.map((o) => (
            <motion.button key={o.id} onClick={() => setEditingId(o.id)} whileHover={{ y: -4 }}
              className="group relative overflow-hidden rounded-2xl border border-white/8 bg-card/40 p-4 text-left transition-colors hover:border-violet-400/30">
              <div className="absolute inset-x-0 top-0 h-1" style={{ background: `linear-gradient(90deg, ${o.colorPrimario || '#a855f7'}, ${o.colorSecundario || '#000'})` }} />
              <div className="flex items-center gap-3">
                <Avatar className="h-12 w-12 border border-white/10"><AvatarImage src={o.escudoUrl} /><AvatarFallback>{o.nombreCompleto?.charAt(0)}</AvatarFallback></Avatar>
                <div className="min-w-0 flex-1"><p className="truncate font-semibold">{o.nombreCompleto}</p><p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground"><Users className="h-3 w-3" />{(o.gestores || []).length} gestor(es)</p></div>
                <Pencil className="h-4 w-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
              </div>
            </motion.button>
          ))}
        </div>
      )}

      {editing && <OrgEditor org={editing} onClose={() => setEditingId(null)} onChanged={refetch} />}
    </div>
  );
}
